import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import TitleHeader from "../../../components/CustomComponents/title";
import { useTranslation } from "react-i18next";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  TablePagination,
  Menu,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import landingApi from "../../../contexts/landingApiClient";
import ConfirmationModal from "../../../components/CustomComponents/ConfirmationModal";
import EditAffiliateModal from "../../../components/affiliate/EditAffiliateModal";
import StatusLabel from "../../../components/StatusLabel";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Affiliate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilter, setSearchFilter] = useState("email");
  const [affiliates, setAffiliates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalAffiliates, setTotalAffiliates] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [affiliateToDelete, setAffiliateToDelete] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [affiliateToEdit, setAffiliateToEdit] = useState(null);

  const getAffiliateData = async () => {
    setIsLoading(true);
    try {
      const params = {
        populate: ["commissionDetails", "payoutDetails"],
        "pagination[page]": page + 1,
        "pagination[pageSize]": rowsPerPage,
      };

      if (searchQuery) {
        if (searchFilter === "status") {
          params[`filters[status][$eq]`] = searchQuery;
        } else {
          params[`filters[${searchFilter}][$containsi]`] = searchQuery;
        }
      }

      const response = await landingApi.get("/affiliates", { params });
      if (response.data && Array.isArray(response.data.data)) {
        setAffiliates(response.data.data);
        setTotalAffiliates(response.data.meta.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching affiliate data:", error);
      toast.error(t("toast_fetch_affiliate_error"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAffiliateData();
  }, [page, rowsPerPage, searchTrigger]);

  const handleSearch = () => {
    setPage(0);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setAffiliateToEdit(selectedRow);
    setIsEditModalOpen(true);
    handleClose();
  };

  const handleDeleteClick = () => {
    setAffiliateToDelete(selectedRow);
    setIsModalOpen(true);
    handleClose();
  };

  const handleConfirmDelete = async () => {
    if (!affiliateToDelete) return;

    try {
      await landingApi.delete(`/affiliates/${affiliateToDelete.id}`);
      toast.success(t("toast_delete_affiliate_success"));
      getAffiliateData(); // Refresh data
    } catch (error) {
      console.error("Error deleting affiliate:", error);
      toast.error(t("toast_delete_affiliate_error"));
    } finally {
      setIsModalOpen(false);
      setAffiliateToDelete(null);
    }
  };

  const handleClick = (event, selectedData) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(selectedData);
  };

  const headCells = [
    { id: "name", label: "name" },
    { id: "email", label: "email" },
    { id: "status", label: "status" },
    { id: "referralCode", label: "referral_code" },
    { id: "totalCommission", label: "total_commission" },
    { id: "totalPaid", label: "total_paid" },
    { id: "currentMonthEarning", label: "current_month_earning" },
    { id: "totalReferrals", label: "total_referrals" },
    { id: "actions", label: "actions" },
  ];

  return (
    <Box>
      <ToastContainer />
      <TitleHeader
        title={t("affiliates")}
        subHeading={t("affiliates_subheading")}
      />
      <Grid container spacing={2} sx={{ my: 2 }} alignItems="center">
        <Grid item xs={3}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>{t("filter_by")}</InputLabel>
            <Select
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              label={t("filter_by")}
            >
              <MenuItem value="email">{t("email")}</MenuItem>
              <MenuItem value="referralCode">{t("referral_code")}</MenuItem>
              <MenuItem value="status">{t("status")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={7}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t("search_user")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSearch}
            sx={{ height: "56px" }}
          >
            {t("search")}
          </Button>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 6, mb: 8 }} />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleEditClick}>{t("edit")}</MenuItem>
        <MenuItem onClick={handleDeleteClick}>{t("delete")}</MenuItem>
      </Menu>
      <ConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t("confirm_deletion")}
        description={t("delete_affiliate_confirmation", {
          email: affiliateToDelete?.attributes?.email,
        })}
      />
      <EditAffiliateModal
        open={isEditModalOpen}
        handleClose={() => setIsEditModalOpen(false)}
        affiliate={affiliateToEdit}
        onAffiliateUpdated={() => {
          getAffiliateData();
          toast.success(t("toast_update_affiliate_success"));
        }}
      />
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id}>{t(headCell.label)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {affiliates.length > 0 ? (
                affiliates.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.attributes.name || "-"}</TableCell>
                    <TableCell>
                      <Link
                        to={`/admin/affiliate/${item.id}`}
                        style={{
                          textDecoration: "underline",
                          color: "#1976d2",
                        }}
                      >
                        {item.attributes.email}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusLabel status={item.attributes.status} />
                    </TableCell>
                    <TableCell>{item.attributes.referralCode}</TableCell>
                    <TableCell>
                      {item.attributes.commissionDetails?.totalCommission !=
                      null
                        ? `€${item.attributes.commissionDetails.totalCommission}`
                        : "€0"}
                    </TableCell>
                    <TableCell>
                      {item.attributes.commissionDetails?.totalPaid != null
                        ? `€${item.attributes.commissionDetails.totalPaid}`
                        : "€0"}
                    </TableCell>
                    <TableCell>
                      {item.attributes.commissionDetails?.currentMonthEarning !=
                      null
                        ? `€${item.attributes.commissionDetails.currentMonthEarning}`
                        : "€0"}
                    </TableCell>
                    <TableCell>
                      {item.attributes.commissionDetails?.totalReferrals || 0}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(event) => handleClick(event, item)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center">
                    <Typography
                      variant="body1"
                      sx={{ color: "text.secondary" }}
                    >
                      {t("no_affiliates_found")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {affiliates.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalAffiliates}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t("rows_per_page")}
            />
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default Affiliate;
